import React, { useState, useEffect, useMemo } from 'react';
import { RefreshCw, Star } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import ProductCard from '../components/ProductCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BestsellersPage = () => {
  const { t, getLocalizedText, language } = useLanguage();
  const [sortBy, setSortBy] = useState('default');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products/bestsellers`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching bestsellers:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const sortedProducts = useMemo(() => {
    const filtered = [...products];
    switch (sortBy) {
      case 'price-asc':
        return filtered.sort((a, b) => (a.salePrice || a.price) - (b.salePrice || b.price));
      case 'price-desc':
        return filtered.sort((a, b) => (b.salePrice || b.price) - (a.salePrice || a.price));
      case 'name-asc':
        return filtered.sort((a, b) => getLocalizedText(a.name).localeCompare(getLocalizedText(b.name)));
      case 'name-desc':
        return filtered.sort((a, b) => getLocalizedText(b.name).localeCompare(getLocalizedText(a.name)));
      default:
        return filtered;
    }
  }, [sortBy, getLocalizedText, products]);

  return (
    <div className="min-h-screen bg-black py-8 px-4" data-testid="bestsellers-page">
      <div className="container mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-xl">
              <Star className="h-7 w-7 md:h-8 md:w-8 text-black fill-black" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white">{t.products.bestsellers}</h1>
          </div>
          <p className="text-zinc-400 text-sm md:text-base">
            {language === 'hu'
              ? 'A legkedveltebb termékeink, amiket vásárlóink a legjobban ajánlanak.'
              : 'Our most loved products, most recommended by our customers.'}
          </p>
        </div>

        {/* Sort Filter */}
        <div className="flex flex-wrap gap-4 mb-8 p-4 bg-zinc-900 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-sm text-zinc-400">{t.filters.sortBy}:</span>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px] bg-zinc-800 border-zinc-700 text-white" data-testid="bestsellers-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="default" className="text-white hover:bg-zinc-700">{t.filters.all}</SelectItem>
                <SelectItem value="price-asc" className="text-white hover:bg-zinc-700">{t.filters.priceAsc}</SelectItem>
                <SelectItem value="price-desc" className="text-white hover:bg-zinc-700">{t.filters.priceDesc}</SelectItem>
                <SelectItem value="name-asc" className="text-white hover:bg-zinc-700">{t.filters.nameAsc}</SelectItem>
                <SelectItem value="name-desc" className="text-white hover:bg-zinc-700">{t.filters.nameDesc}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-20">
            <RefreshCw className="h-8 w-8 animate-spin text-zinc-500 mx-auto mb-4" />
            <p className="text-zinc-500">{t.common.loading}</p>
          </div>
        ) : sortedProducts.length === 0 ? (
          <div className="text-center py-20">
            <Star className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400 text-lg">
              {language === 'hu' ? 'Jelenleg nincsenek kiemelt termékek.' : 'No bestseller products yet.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
            {sortedProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BestsellersPage;
